import logger from '@/layers/support/monitoring/logger';
import { Program } from '@/types/models';
import { NewProgramRequest, ProgramFilters } from '@/types/programs';
import { fetchWrapper } from '../utils/fetchWrapper';

const API_URL = '/api/programs';
const LOG_MODULE = 'FE [Presentation / Services / programs]';

const ENABLE_CLIENT_CACHE = process.env.NODE_ENV !== 'development';

type CacheEntry<T> = {
  value: T;
  expiresAtMs: number;
};

const PROGRAMS_LIST_TTL_MS = 24 * 60 * 60 * 1000; // 1 day
const PROGRAM_BY_ID_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

const programsListCache = new Map<string, CacheEntry<Program[]>>();
const programsListInFlight = new Map<string, Promise<Program[]>>();

const programByIdCache = new Map<number, CacheEntry<Program>>();
const programByIdInFlight = new Map<number, Promise<Program>>();

function nowMs() {
  return Date.now();
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts = keys.map(key => `${JSON.stringify(key)}:${stableStringify(obj[key])}`);
  return `{${parts.join(',')}}`;
}

function getProgramsListCacheKey(filters?: ProgramFilters) {
  const encoded = encodeURIComponent(stableStringify(filters || {}));
  return `${API_URL}?filters=${encoded}`;
}

function getFreshCacheValue<T>(entry?: CacheEntry<T>) {
  if (!entry) return null;
  if (entry.expiresAtMs <= nowMs()) return null;
  return entry.value;
}

function invalidateProgramsListsCache() {
  programsListCache.clear();
  programsListInFlight.clear();
}

function invalidateProgramByIdCache(id: number) {
  programByIdCache.delete(id);
  programByIdInFlight.delete(id);
}

export const fetchPrograms = async (filters?: ProgramFilters): Promise<Program[]> => {
  logger.debug(`${LOG_MODULE} fetchPrograms: begin`, { filters });

  try {
    const url = getProgramsListCacheKey(filters);
    if (ENABLE_CLIENT_CACHE) {
      const cached = getFreshCacheValue(programsListCache.get(url));
      if (cached) {
        logger.debug(`${LOG_MODULE} fetchPrograms: cache hit`, { url, resultCount: cached.length });
        return cached;
      }
    }

    const existingInFlight = programsListInFlight.get(url);
    if (existingInFlight) {
      logger.debug(`${LOG_MODULE} fetchPrograms: dedupe in-flight`, { url });
      return await existingInFlight;
    }

    const requestPromise = fetchWrapper
      .get<Program[]>(url)
      .then(result => {
        if (ENABLE_CLIENT_CACHE) {
          programsListCache.set(url, {
            value: result,
            expiresAtMs: nowMs() + PROGRAMS_LIST_TTL_MS,
          });
        }
        return result;
      })
      .finally(() => {
        programsListInFlight.delete(url);
      });

    programsListInFlight.set(url, requestPromise);
    const result = await requestPromise;

    logger.debug(`${LOG_MODULE} fetchPrograms: end`, { resultCount: result.length });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchPrograms: error`, { error });
    throw error;
  }
};

export const fetchProgramById = async (id: number): Promise<Program> => {
  logger.debug(`${LOG_MODULE} fetchProgramById: begin`, { id });

  try {
    if (ENABLE_CLIENT_CACHE) {
      const cached = getFreshCacheValue(programByIdCache.get(id));
      if (cached) {
        logger.debug(`${LOG_MODULE} fetchProgramById: cache hit`, { id });
        return cached;
      }
    }

    const existingInFlight = programByIdInFlight.get(id);
    if (existingInFlight) {
      logger.debug(`${LOG_MODULE} fetchProgramById: dedupe in-flight`, { id });
      return await existingInFlight;
    }

    const requestPromise = fetchWrapper
      .get<Program>(`${API_URL}/${id}`)
      .then(result => {
        if (ENABLE_CLIENT_CACHE) {
          programByIdCache.set(id, { value: result, expiresAtMs: nowMs() + PROGRAM_BY_ID_TTL_MS });
        }
        return result;
      })
      .finally(() => {
        programByIdInFlight.delete(id);
      });

    programByIdInFlight.set(id, requestPromise);
    const result = await requestPromise;

    logger.debug(`${LOG_MODULE} fetchProgramById: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchProgramById: error`, { error });
    throw error;
  }
};

export const createProgram = async (program: NewProgramRequest): Promise<Program> => {
  logger.debug(`${LOG_MODULE} createProgram: begin`, { program });

  try {
    const result = await fetchWrapper.post<Program>(API_URL, program);
    if (ENABLE_CLIENT_CACHE) {
      invalidateProgramsListsCache();
    }

    logger.debug(`${LOG_MODULE} createProgram: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} createProgram: error`, { error });
    throw error;
  }
};

export const updateProgram = async (id: string, program: Partial<Program>): Promise<Program> => {
  logger.debug(`${LOG_MODULE} updateProgram: begin`, { id, program });

  try {
    const result = await fetchWrapper.put<Program>(`${API_URL}/${id}`, program);
    if (ENABLE_CLIENT_CACHE) {
      invalidateProgramsListsCache();
      invalidateProgramByIdCache(Number(id));
    }

    logger.debug(`${LOG_MODULE} updateProgram: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} updateProgram: error`, { error });
    throw error;
  }
};

export const deleteProgram = async (id: string): Promise<void> => {
  logger.debug(`${LOG_MODULE} deleteProgram: begin`, { id });

  try {
    await fetchWrapper.delete<void>(`${API_URL}/${id}`);
    if (ENABLE_CLIENT_CACHE) {
      invalidateProgramsListsCache();
      invalidateProgramByIdCache(Number(id));
    }

    logger.debug(`${LOG_MODULE} deleteProgram: end`);
  } catch (error) {
    logger.error(`${LOG_MODULE} deleteProgram: error`, { error });
    throw error;
  }
};
